import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      
      // Check if user is admin
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin credentials required.');
        return;
      }
      
      toast.success('Welcome, Administrator!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0D5EAF 0%, #094078 100%)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
        <div className="text-center text-white">
          <div className="flex items-center justify-center gap-6 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
              alt="AHEPA Logo" 
              className="w-28 h-28 object-contain rounded-full bg-white p-2"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
              alt="Daughters of Penelope Logo" 
              className="w-28 h-28 object-contain rounded-full bg-white p-2"
            />
          </div>
          <h2 className="font-heading text-4xl font-bold mb-4">Administration Portal</h2>
          <p className="text-white/80 text-lg max-w-md mx-auto">
            Manage scholarships, review applications, and oversee the entire scholarship program.
          </p>
          <div className="mt-8 p-4 bg-white/10 rounded-sm border border-white/20">
            <p className="text-sm text-white/70">
              This portal is restricted to authorized administrators only.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-greek-blue mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: '#0D5EAF' }}>
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold" style={{ color: '#0D5EAF' }}>Admin Portal</h1>
                <p className="text-xs text-slate-500">Scholarship Management</p>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Administrator Login</h2>
            <p className="text-slate-600 mt-2">Sign in with your admin credentials</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ahepa.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-slate-300"
                data-testid="admin-login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-sm border-slate-300 pr-12"
                  data-testid="admin-login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-white rounded-sm font-semibold tracking-wide uppercase"
              style={{ background: '#0D5EAF' }}
              data-testid="admin-login-submit-btn"
            >
              {loading ? 'Signing In...' : 'Sign In as Administrator'}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-slate-50 rounded-sm border border-slate-200">
            <p className="text-sm text-slate-600 text-center">
              <Shield className="w-4 h-4 inline mr-1" />
              For applicant login, please use the{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: '#0D5EAF' }}>
                main login page
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
